﻿// CUSTOM: personalised-utils.js
// Core utilities for the personalised bundle system

(function() {
  // Ensure namespace exists
  window.personalised = window.personalised || {};

  /**
   * Formats an ID for Recharge external ID fields
   * @param {string|number} id - The ID to format
   * @returns {Object} Formatted ID object for Recharge
   */
  function formatExternalId(id) {
    return {
      ecommerce: String(id)
    };
  }

  /**
   * Formats the bundle items for Recharge's updateBundleSelection API call.
   * Uses a static collection_id and collection_source defined in config,
   * corresponding to the "Personalised" Recharge bundle collection.
   * 
   * @param {Array} items - The bundle items to format
   * @returns {Array} Formatted items for Recharge
   */
  function formatBundleItems(items) {
    // Pull config values safely
    const collectionId = window.personalised?.config?.bundle?.RECHARGE?.COLLECTION_ID || '512297271595';
    const collectionSource = window.personalised?.config?.bundle?.RECHARGE?.COLLECTION_SOURCE || 'shopify';
    
    return items
      .filter(item => item.product_id && item.variant_id)
      .map(item => ({
        external_product_id: String(item.product_id),
        external_variant_id: String(item.variant_id),
        collection_id: collectionId,
        collection_source: collectionSource,
        quantity: item.quantity
      }));
  }

  /**
   * Gets the current subscription details from metadata storage
   * This function provides consistent access to subscription data
   * 
   * @returns {Object|null} Subscription details object or null if not available
   */
  function getSubscriptionDetails() {
    // Get subscription details from metadata storage
    const storageManager = window.personalised?.storageManager;
    if (storageManager) {
      const storedDetails = storageManager.getMetadata('subscriptionDetails', null);
      if (storedDetails && typeof storedDetails === 'object') {
        // Add timestamp if missing (to track data freshness)
        if (!storedDetails.lastFetched) {
          storedDetails.lastFetched = Date.now();
        }
        return storedDetails;
      }
    }
    
    // Nothing found
    if (window.personalised?.debugMode) console.debug('[Utils] No subscription details found in metadata storage');
    return null;
  }

  /**
   * Extracts the essential subscription data fields needed for business logic
   * This helps standardize the subscription data format during the migration phase
   * 
   * @param {Object} fullSubscription - The complete subscription data object from Recharge
   * @returns {Object} Simplified subscription data with only essential fields
   */
  function extractEssentialSubscriptionData(fullSubscription) {
    if (!fullSubscription || typeof fullSubscription !== 'object') {
      if (window.personalised?.debugMode) console.warn('[Utils] Invalid subscription data provided to extractEssentialSubscriptionData');
      return null;
    }
    
    return {
      // Convert id to string to match metadata schema expectations
      id: fullSubscription.id ? String(fullSubscription.id) : null,
      status: fullSubscription.status || 'UNKNOWN',
      order_interval_frequency: fullSubscription.order_interval_frequency || null,
      purchaseItemId: fullSubscription.purchaseItemId || null,
      bundleSelectionId: fullSubscription.bundleSelectionId || null,
      lastFetched: Date.now() // Add timestamp to track data freshness
    };
  }

  /**
   * Refreshes subscription details from Recharge API and stores them in metadata
   * Uses the extractEssentialSubscriptionData helper to standardize the data format
   * 
   * @param {string} subscriptionId - The Recharge subscription ID to fetch
   * @returns {Promise<Object|null>} The refreshed subscription data or null if fetch failed
   */
  async function refreshSubscriptionDetails(subscriptionId) {
    if (!subscriptionId) {
      if (window.personalised?.debugMode) console.warn('[Utils] No subscription ID provided to refreshSubscriptionDetails');
      return null;
    }
    
    // Ensure rechargeUtils is available
    if (typeof window.personalised?.rechargeUtils?.getSubscription !== 'function') {
      if (window.personalised?.debugMode) console.warn('[Utils] rechargeUtils.getSubscription not available');
      return null;
    }
    
    try {
      // Fetch subscription details from Recharge
      const fullSubscription = await window.personalised.rechargeUtils.getSubscription(subscriptionId);
      if (!fullSubscription || typeof fullSubscription !== 'object') {
        if (window.personalised?.debugMode) console.warn('[Utils] Failed to fetch subscription details from Recharge');
        return null;
      }
      
      // Extract essential data
      const essentialData = extractEssentialSubscriptionData(fullSubscription);
      if (!essentialData) {
        return null;
      }
      
      // Store in metadata (if storageManager is available)
      const storageManager = window.personalised?.storageManager;
      if (storageManager && typeof storageManager.setMetadata === 'function') {
        storageManager.setMetadata('subscriptionDetails', essentialData);
      }
      
      // Return the essential data
      return essentialData;
    } catch (err) {
      console.error('[Utils] Error refreshing subscription details:', err);
      return null;
    }
  }

  // Initialize format utils in namespace early
  window.personalised.utils = window.personalised.utils || {};
  window.personalised.utils.formatExternalId = formatExternalId;
  window.personalised.utils.formatBundleItems = formatBundleItems;
  window.personalised.utils.getSubscriptionDetails = getSubscriptionDetails;
  window.personalised.utils.extractEssentialSubscriptionData = extractEssentialSubscriptionData;
  window.personalised.utils.refreshSubscriptionDetails = refreshSubscriptionDetails;

  // Utilities implementation

  const utils = (function() {

    // Debounce function to limit how frequently operations can fire
    function debounce(func, wait = 800, immediate = false) {
      let timeout;
      return function(...args) {
        const context = this;
        const later = function() {
          timeout = null;
          if (!immediate) func.apply(context, args);
        };
        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
      };
    }
  
    // Simple sync state tracking
    const syncState = {
      syncInProgress: false
    };
    
    // Mark sync as started
    function startSync() {
      syncState.syncInProgress = true;
      console.time('bundleSync');
    }
    
    // Mark sync as completed
    function completeSync() {
      syncState.syncInProgress = false;
      console.timeEnd('bundleSync');
    }
    
    /**
     * Get the current bundle mode based on multiple sources with persistence
     * 
     * Priority order:
     * 1. URL parameters (mode=edit with subscription_id)
     * 2. Session storage (if previously set by this function)
     * 3. Customer state (logged in = NEW, otherwise GUEST)
     * 
     * When in EDIT mode, saves mode and subscription_id to sessionStorage for persistence across pages
     * 
     * @returns {'GUEST'|'NEW'|'EDIT'} The determined mode
     */
    const VALID_MODES = ['edit', 'new', 'guest'];
    
    /**
     * Gets the current mode from session storage with fallbacks
     * @returns {string} The current mode ('edit', 'new', or 'guest')
     */
    function getMode() {
      // Using storageManager to get mode
      const mode = window.personalised.storageManager.getMode();
      
      if (window.personalised?.config?.debugMode) {
        console.debug(`[Mode Getter] Returning mode from storageManager: '${mode}'`);
      }
      
      return mode;
    }
    
    function getCurrentSubscriptionId() {
      // Using storageManager to get subscription ID
      return window.personalised.storageManager.getModeSubscriptionId();
    }

    /**
     * Sets the current mode and persists it to session storage via storageManager
     * The mode is now solely managed by the storageManager and no longer stored as a global variable
     * 
     * @param {string} mode - The mode to set ('edit', 'new', or 'guest')
     * @param {Object} options - Additional options
     * @param {string} [options.source] - Source of the mode change for debugging
     * @param {string|null} [options.subscriptionId] - Associated subscription ID if in edit mode
     */
    function setCurrentMode(mode, options = {}) {
      const normalised = String(mode).toLowerCase();
      const source = options.source || 'unknown';
      const subscriptionId = options.subscriptionId || null;

      if (!VALID_MODES.includes(normalised)) {
        console.warn(`[Mode Setter] Invalid mode '${mode}' â€“ defaulting to guest`);
        mode = 'guest';
      }

      const data = {
        mode: normalised,
        timestamp: Date.now(),
        source,
        ...(subscriptionId && { subscriptionId }),
      };

      try {
        // Using storageManager to set mode
        window.personalised.storageManager.setMode(normalised, { source, subscriptionId });
        
        // Log mode changes with context
        if (window.personalised?.config?.debugMode) {
          console.debug(`[Mode Setter] Mode set to '${normalised}' via ${source}`, { 
            mode: normalised, 
            previousMode: window.personalised._previousMode || 'unknown',
            source,
            subscriptionId: subscriptionId || 'none',
            timestamp: new Date().toISOString()
          });
        }
        
        // Always log mode transitions since they're critical for debugging
        console.info(`[Mode Transition] Mode set to '${normalised}' with subscription ID: ${subscriptionId || 'none'} (source: ${source})`);
        
        // Track previous mode for debugging
        window.personalised._previousMode = normalised;
      } catch (e) {
        console.warn('[Mode Setter] Failed to access sessionStorage:', e);
      }
    }

    // Public API
    return {
      debounce,
      syncState,
      startSync,
      completeSync,
      getMode,
      getCurrentSubscriptionId,
      setCurrentMode,
      // We'll assign the correct implementation to window.personalised.utils.initMode later
      // Don't add a reference here to avoid circular reference issues
      // trackLoginState removed - now using personalisedCore.loginTracking
      validateMetadataIntegrity,
      handleAuthError
    };
  })();

  // Merge inner utils with namespace (eventBus and rechargeUtils now in separate modules)
  Object.assign(window.personalised.utils, utils);

  /**
   * Safely run a callback when document.body is available
   * @param {Function} callback - The function to run when document.body is available
   */
  function whenBodyReady(callback) {
    if (typeof callback !== 'function') {
      console.error('[DOM Utils] whenBodyReady requires a function callback');
      return;
    }
    
    // If body is already available, run immediately
    if (document.body) {
      callback(document.body);
      return;
    }
    
    // Otherwise wait for DOM content loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        if (document.body) {
          callback(document.body);
        }
      });
    } else {
      // Use MutationObserver as a last resort if document is loaded but body not available
      // (rare edge case)
      const observer = new MutationObserver(() => {
        if (document.body) {
          callback(document.body);
          observer.disconnect();
        }
      });
      
      observer.observe(document.documentElement, { childList: true });
    }
  }
  
  /**
   * Check if user is currently logged in
   * Uses personalisedCore implementation when available
   * @returns {boolean} True if user is logged in, false otherwise
   */
  function isUserLoggedIn() {
    if (window.personalisedCore?.loginTracking?.isUserLoggedIn) {
      return window.personalisedCore.loginTracking.isUserLoggedIn();
    }
    
    // Fallback implementation if core isn't available
    if (document.body) {
      return document.body.dataset.customerLoggedIn === 'true';
    }
    
    return localStorage.getItem('personalised_user_logged_in') === 'true';
  }

  // Add functions to the utils namespace
  Object.assign(window.personalised.utils, {
    whenBodyReady,
    isUserLoggedIn
  });

  // All storage access now goes through storageManager

  /**
   * Validates that all required metadata exists for edit mode
  
  
   * If metadata is missing but subscription ID is available, attempts to recover from Recharge
   * @returns {Promise<boolean>} True if metadata is valid or was successfully recovered
   */
  async function validateMetadataIntegrity() {
    try {
      // Only check metadata in edit mode
      const mode = window.personalised.utils.getMode();
      if (mode !== window.personalised.config.modes.EDIT) {
        return true; // Not in edit mode, no validation needed
      }
      
      // Get subscription ID from session
      const subscriptionId = window.personalised.utils.getCurrentSubscriptionId();
      if (!subscriptionId) {
        console.warn('[Metadata Integrity] In edit mode but no subscription ID found');
        return false;
      }
      
      // Check if critical metadata is missing
      const hasBundleSelectionId = window.personalised.bundle.hasMetadata('bundleSelectionId');
      const hasSubscriptionId = window.personalised.bundle.hasMetadata('subscriptionId');
      const hasPurchaseItemId = window.personalised.bundle.hasMetadata('purchaseItemId');
      
      const metadataIntact = hasBundleSelectionId && hasSubscriptionId && hasPurchaseItemId;
      
      // If metadata is intact, no recovery needed
      if (metadataIntact) {
        console.debug('[Metadata Integrity] All critical metadata is present');
        return true;
      }
      
      // Log the missing metadata for debugging
      console.warn('[Metadata Integrity] Missing critical metadata detected:', {
        hasBundleSelectionId,
        hasSubscriptionId,
        hasPurchaseItemId,
        mode,
        subscriptionId
      });
      
      // Attempt to recover metadata from Recharge
      console.log('[Metadata Integrity] Attempting to recover metadata from Recharge');
      
      if (!window.personalised.rechargeUtils?.getBundleSelectionForSubscription) {
        console.error('[Metadata Integrity] Cannot recover metadata: rechargeUtils not available');
        return false;
      }
      
      // Get fresh data from Recharge
      const bundleData = await window.personalised.rechargeUtils.getBundleSelectionForSubscription(subscriptionId);
      
      if (!bundleData) {
        console.error('[Metadata Integrity] Failed to fetch bundle data from Recharge');
        return false;
      }
      
      // Store critical metadata
      if (bundleData.bundle) {
        window.personalised.bundle.setMetadata('bundleSelectionId', bundleData.bundle.id);
        window.personalised.bundle.setMetadata('purchaseItemId', bundleData.bundle.purchaseItemId);
        window.personalised.bundle.setMetadata('externalProductId', bundleData.bundle.external_product_id);
        window.personalised.bundle.setMetadata('externalVariantId', bundleData.bundle.external_variant_id);
        
        if (bundleData.bundle.bundleRechargeId) {
          window.personalised.bundle.setMetadata('bundleRechargeId', bundleData.bundle.bundleRechargeId);
        }
      }
      
      if (bundleData.subscription) {
        window.personalised.bundle.setMetadata('subscriptionId', bundleData.subscription.id);
        window.personalised.bundle.setMetadata('originalFrequency', bundleData.subscription.order_interval_frequency);
      }
      
      console.log('[Metadata Integrity] Successfully recovered metadata from Recharge');
      
      // Verify recovery was successful
      const recoverySuccessful = 
        window.personalised.bundle.hasMetadata('bundleSelectionId') &&
        window.personalised.bundle.hasMetadata('subscriptionId') &&
        window.personalised.bundle.hasMetadata('purchaseItemId');
      
      return recoverySuccessful;
    } catch (err) {
      console.error('[Metadata Integrity] Error validating metadata integrity:', err);
      return false;
    }
  }
  
  // Add validateMetadataIntegrity to the utils namespace
  Object.assign(window.personalised.utils, { validateMetadataIntegrity });
  
  /**
   * Handles authentication errors by clearing bundle data
   * Should be called when API calls fail with authentication errors
   * @param {Error} error - The error that occurred
   * @param {string} source - Source of the error for logging
   * @returns {boolean} True if handled as authentication error
   */
  function handleAuthError(error, source = 'unknown') {
    try {
      // Check if error is authentication-related
      const errorMessage = error?.message || String(error);
      const isAuthError =
        errorMessage.includes('authentication') ||
        errorMessage.includes('unauthorized') ||
        errorMessage.includes('not logged in') ||
        errorMessage.includes('auth') ||
        errorMessage.includes('401');

      if (!isAuthError) {
        return false; // Not an authentication error
      }

      console.warn(`[Auth Error] Authentication error detected in ${source}:`, errorMessage);

      // Clear bundle data
      if (window.personalised?.bundle?.teardown) {
        window.personalised.bundle.teardown();
        console.log('[Auth Error] Bundle data cleared due to authentication error');

        // Also clear customer ID to force redetection (PR-4.3: route through storageManager)
        window.personalised.storageManager?.clearCustomerId();

        return true;
      }

      return false;
    } catch (err) {
      console.error('[Auth Error] Error in handleAuthError:', err);
      return false;
    }
  }
  
  // Add handleAuthError to the utils namespace
  Object.assign(window.personalised.utils, { handleAuthError });

  // fullInitMode and placeholder replacement
  // (portal utils isPortalUrl, checkForPortalReturn, refreshSessionTokensOnly are
  //  registered by personalised-recharge-session.js which loads before this file)
  if (window.personalised?.utils) {
    // IMPORTANT: Define fullInitMode before we try to use it
    // This is the full implementation that will replace the placeholder
    /**
     * Initializes the user's interaction mode ("guest" or "edit").
     * Guarantees safe, memoized, idempotent async initialization.
     * Ensures Recharge SDK and session are available before subscription checks.
     *
     * @returns {Promise<'guest' | 'edit'>} The resolved user mode.
     */
    const fullInitMode = async function() {
      if (window.personalised.debugMode) console.debug('[initMode] Called - checking current mode');
      
      // Check if we already have a mode in storage
      if (window.personalised.storageManager.hasStoredMode()) {
        const currentMode = window.personalised.utils.getMode();
        if (window.personalised.debugMode) console.debug(`[initMode] Mode found in storage: ${currentMode}`);
        return currentMode;
      }
      
      if (window.personalised.debugMode) console.debug('[initMode] No stored mode found, detecting mode...');
      
      // Check authentication status
      const isAuthenticated = window.personalised.sessionManager?.isAuthenticatedSession() || 
                            !!window.personalised.customerIdManager.get() ||
                            (document.body?.dataset?.customerLoggedIn === 'true');
      
      if (!isAuthenticated) {
        // No authentication = guest mode
        if (window.personalised.debugMode) console.info('[Mode] Setting mode to guest (no authentication)');
        window.personalised.utils.setCurrentMode('guest', { source: 'ensureModeInitialized:noAuth' });
        return 'guest';
      }
      
      // We have an authenticated user, check for subscription
      try {
        // 1. Wait for Recharge SDK to be ready
        if (typeof window.personalised.utils.waitForRechargeSDK === 'function') {
          if (window.personalised.debugMode) console.debug('[initMode] Waiting for Recharge SDK...');
          try {
            await window.personalised.utils.waitForRechargeSDK();
          } catch (sdkError) {
            console.error('[initMode] Recharge SDK failed to initialize:', sdkError);
            throw new Error('Recharge SDK not available');
          }
        }
        
        // Verify SDK is available after waiting
        if (!window.recharge?.auth) {
          throw new Error('Recharge SDK auth module not available');
        }
        
        // 2. Validate/establish Recharge session using loginShopifyAppProxy (SDK v1.38.0)
        let session = null;
        try {
          if (window.personalised.debugMode) console.debug('[initMode] Attempting to get session via loginShopifyAppProxy...');
          session = await window.recharge.auth.loginShopifyAppProxy();
        } catch (loginError) {
          console.error('[initMode] Failed to establish Recharge session:', loginError);
          throw new Error('Unable to establish Recharge session');
        }
        if (!session) {
          throw new Error('Failed to obtain valid Recharge session');
        }
        
        // 3. Check for subscription with retry
        if (typeof window.personalised.rechargeUtils?.findPersonalisedPackSubscription === 'function') {
          let attempts = 0;
          let lastError;
          
          while (attempts < 3) {
            try {
              if (attempts > 0) {
                if (window.personalised.debugMode) console.debug(`[initMode] Retrying subscription check (attempt ${attempts + 1})...`);
                await new Promise(resolve => setTimeout(resolve, 1000));
              }
              
              const pack = await window.personalised.rechargeUtils.findPersonalisedPackSubscription();
              
              if (pack) {
                // Active subscription found â€” edit mode
                const _dbgEdit = window.personalised?.debug;
                if (_dbgEdit) {
                  _dbgEdit.trace('subscription:lookupResult', {
                    found: true,
                    status: pack.status,
                    subscriptionId: pack.id
                  });
                }
                if (window.personalised.debugMode) console.info(`[Mode] Setting mode to edit with subscription ID: ${pack.id}`);
                window.personalised.utils.setCurrentMode('edit', { 
                  source: 'ensureModeInitialized:subscription', 
                  subscriptionId: pack.id
                });
                return 'edit';
              }
              
              // No active subscription found, no need to retry
              break;
              
            } catch (error) {
              lastError = error;
              attempts++;
              if (attempts === 3) {
                console.error('[initMode] All subscription check retries failed:', error);
                throw error;
              }
            }
          }
          
          // No active subscription found â€” distinguish "no subscription history" from "inactive subscription"
          // PR-5.3: Mode is now a routing concept only. Any customer with subscription history
          // (active OR inactive) enters 'edit' mode and routes to /pages/manage-pack.
          // Only truly no-history customers remain in 'new' mode on /pages/personalised-review.
          const _dbg = window.personalised?.debug;
          const _hasInactiveSub = window.personalised.storageManager?.getMetadata('hasInactiveSubscription');
          if (_hasInactiveSub) {
            const _inactiveId = window.personalised.storageManager.getMetadata('inactiveSubscriptionId');
            const _inactiveStatus = window.personalised.storageManager.getMetadata('inactiveSubscriptionStatus');
            if (_dbg) {
              _dbg.trace('review:modeDetected', {
                mode: 'edit',
                reason: 'inactiveSubscription',
                inactiveSubscriptionId: _inactiveId,
                inactiveSubscriptionStatus: _inactiveStatus
              });
            }
            if (window.personalised.debugMode) console.info('[Mode] Setting mode to edit (authenticated, inactive subscription detected)');
            window.personalised.utils.setCurrentMode('edit', {
              source: 'ensureModeInitialized:inactiveSubscription',
              subscriptionId: _inactiveId
            });
            return 'edit';
          } else {
            if (_dbg) {
              _dbg.trace('review:modeDetected', { mode: 'new', reason: 'noSubscription' });
            }
            if (window.personalised.debugMode) console.info('[Mode] Setting mode to new (authenticated without subscription)');
            window.personalised.utils.setCurrentMode('new', { source: 'ensureModeInitialized:noSubscription' });
            return 'new';
          }
        } else {
          // Subscription check not available
          if (window.personalised.debugMode) console.info('[Mode] Setting mode to new (subscription check unavailable)');
          window.personalised.utils.setCurrentMode('new', { source: 'ensureModeInitialized:noSubCheck' });
          return 'new';
        }
      } catch (error) {
        // Log the specific error for debugging
        if (window.personalised.debugMode) {
          console.warn('[Mode] Error during mode initialization:', {
            error,
            hasRechargeSDK: !!window.recharge,
            hasAuthModule: !!window.recharge?.auth,
            hasSession: !!(window.recharge?.auth?.getSession && await window.recharge.auth.getSession().catch(() => null))
          });
        }
        
        // Error but authenticated = new mode
        if (window.personalised.debugMode) console.info('[Mode] Setting mode to new (error during subscription check)');
        window.personalised.utils.setCurrentMode('new', { 
          source: 'ensureModeInitialized:error',
          error: error.message
        });
        return 'new';
      }
    };
    
    // Now that fullInitMode is defined, let's use it to replace the placeholder
    if (window.personalised?.debugMode) {
      console.debug('[Mode] fullInitMode is defined:', typeof fullInitMode === 'function');
    }
    
    // Replace the placeholder initMode from personalised-mode.js with our full implementation
    // Only if it exists to ensure safe overwriting
    if (typeof window.personalised.utils.initMode === 'function') {
      try {
        // Get reference to the original implementation
        const placeholderFunction = window.personalised.utils.initMode.toString();
        
        if (window.personalised?.debugMode) {
          console.debug('[Mode] Current initMode implementation:', placeholderFunction.slice(0, 100) + '...');
        }
        
        // Check if it's still the placeholder (contains 'placeholder' in the function definition)
        const isStillPlaceholder = placeholderFunction.includes('Placeholder') || 
                                  placeholderFunction.includes('placeholder');
        
        if (isStillPlaceholder) {
          if (window.personalised?.debugMode) {
            console.debug('[Mode] Confirmed current implementation is placeholder, replacing with full version');
          }
          
          // Replace with our full implementation
          window.personalised.utils.initMode = fullInitMode;
          
          if (window.personalised?.debugMode) {
            console.info('[Mode] initMode implementation replaced with full version from personalised-utils.js');
          }
        } else {
          // Skip replacement since it's already been replaced
          if (window.personalised?.debugMode) {
            console.debug('[Mode] initMode already has a full implementation, skipping replacement');
          }
        }
      } catch (error) {
        console.error('[Mode] Error while replacing initMode placeholder:', error);
      }
    } else {
      console.error('[Mode] Could not find initMode placeholder from personalised-mode.js - mode functionality may be broken');
    }
  }

})();
